/**
 * Shared streaming processor logic
 * Contains the common chunk processing logic used across Threads, TaskManager, and DashboardManager
 */

/**
 * Main streaming function that handles the complete chat streaming flow
 * @param {Object} config - Configuration object
 * @param {string} config.apiUrl - The API endpoint to stream from
 * @param {Object} config.payload - The request payload
 * @param {Object} config.initialMessage - The initial empty message object
 * @param {Function} config.updateMessages - Function to update messages state
 * @param {Function} config.setShowProcessingMessage - Function to show/hide processing message
 * @param {Function} config.setProcessingMessage - Function to set processing message text
 * @param {Function} config.setStartedTyping - Function to set started typing state
 * @param {Function} config.resetMessages - Function to reset messages on error
 * @param {Object} config.callbacks - Optional callbacks for specific events
 * @returns {Promise} - Resolves when streaming is complete
 */
export const processStream = async ({
  apiUrl,
  payload,
  initialMessage,
  updateMessages,
  setShowProcessingMessage,
  setProcessingMessage,
  setStartedTyping,
  resetMessages,
  callbacks = {},
  setStreamingActionData
}) => {
  // Initialize tracking variables
  const responseChunks = [];
  let currentType = null;
  let currentSubtype = null;
  let currentContent = '';
  let currentAttributes = {};
  let messageKey = initialMessage.timestamp;
  let hasData = false;
  const references = new Set();
  const streamingActions = [];
  // Track report-build state to suppress UI until completion
  const reportBuild = { inProgress: false, snippetShown: false };
  // Track real-time reasoning timing (per reasoning block — resets after each 'rs' done event)
  let reasoningStartTime = 0;    // Date.now() when first delta of current block arrived
  let reasoningChipPushed = false; // whether current block already has a "Thinking..." chip
  let reasoningChipIdx = -1;     // exact index in responseChunks of the current block's chip

  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include'
    });

    // Handle usage limits
    if (response.status === 403) {
      if (callbacks.onUsageLimit) callbacks.onUsageLimit();
      resetMessages({ messageIds: [payload.message?.id, initialMessage.id] });
      return;
    }

    if (!response.body) {
      throw new Error('ReadableStream not supported in this browser.');
    }

    setStartedTyping(true);
    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    
    // Buffer to accumulate incomplete JSON objects
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      if (value) {
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        
        // Process complete JSON objects from buffer
        let startIndex = 0;
        let braceCount = 0;
        let inString = false;
        let escapeNext = false;
        
        for (let i = 0; i < buffer.length; i++) {
          const char = buffer[i];
          
          if (escapeNext) {
            escapeNext = false;
            continue;
          }
          
          if (char === '\\') {
            escapeNext = true;
            continue;
          }
          
          if (char === '"') {
            inString = !inString;
            continue;
          }
          
          if (!inString) {
            if (char === '{') {
              braceCount++;
            } else if (char === '}') {
              braceCount--;
              
              // Complete JSON object found
              if (braceCount === 0) {
                const jsonStr = buffer.substring(startIndex, i + 1);
                try {
                  const parsedChunk = JSON.parse(jsonStr);
                  
                  // Handle data availability notification
                  if (parsedChunk.type === 'hasData') {
                    hasData = true;
                    startIndex = i + 1;
                    continue;
                  }

                  // Handle thread metadata (when backend auto-creates a thread)
                  if (parsedChunk.type === 'thread_metadata') {
                    if (callbacks.onThreadMetadata) {
                      try {
                        callbacks.onThreadMetadata({
                          id: parsedChunk.threadId,
                          title: parsedChunk.title,
                          mode: parsedChunk.mode,
                          forecastId: parsedChunk.forecastId || null
                        });
                      } catch (err) {
                        console.error('Error handling thread metadata:', err);
                      }
                    }
                    startIndex = i + 1;
                    continue;
                  }

                  // 'rs' = reasoning summary done event from backend.
                  // Use arrival timestamp to compute how long the model was thinking,
                  // then update the "Thinking..." chip to "Thought for X seconds."
                  if (parsedChunk.type === 'rs' && parsedChunk.subtype === 'reasoning_summary') {
                    if (reasoningStartTime > 0 && reasoningChipIdx >= 0) {
                      const elapsedSec = Math.max(1, Math.round((Date.now() - reasoningStartTime) / 1000));
                      const label = `Thought for ${elapsedSec} second${elapsedSec !== 1 ? 's' : ''}.`;
                      // Update the exact chip for THIS reasoning block (not findIndex which finds first)
                      responseChunks[reasoningChipIdx] = { type: 'rs', subtype: 'reasoning_summary', content: label };
                      updateMessages((prevMessages) =>
                        prevMessages.map((msg) =>
                          msg.timestamp === messageKey && msg.direction === 'received'
                            ? { ...msg, content: JSON.stringify(responseChunks), hasData, references: Array.from(references) }
                            : msg
                        )
                      );
                    }
                    // Reset so the next reasoning block gets its own fresh chip
                    reasoningStartTime = 0;
                    reasoningChipPushed = false;
                    reasoningChipIdx = -1;
                    startIndex = i + 1;
                    continue;
                  }

                  // Handle processing messages
                  if (parsedChunk.type === 'p') {
                    const ptxt = parsedChunk.txt || '';
                    const pIsSnippet = typeof ptxt === 'string' && ptxt.includes('```report-snippet');
                    
                    // Real-time reasoning delta — fires while the model is thinking.
                    // On the first delta: record the start time and push a "Thinking..." chip.
                    // Subsequent deltas are ignored (we don't show the actual thought content).
                    // The chip is later updated to "Thought for X seconds." when the 'rs' done
                    // event arrives (see handler above).
                    if (parsedChunk.subtype === 'reasoning_delta') {
                      if (!reasoningChipPushed) {
                        // Flush the current buffer so pending tc/tr chunks land
                        // before the reasoning chip in responseChunks
                        if (currentType !== null) {
                          responseChunks.push({
                            type: currentType,
                            subtype: currentSubtype,
                            content: currentContent,
                            ...currentAttributes
                          });
                          currentType = null;
                          currentSubtype = null;
                          currentContent = '';
                          currentAttributes = {};
                        }
                        reasoningStartTime = Date.now();
                        reasoningChipPushed = true;
                        reasoningChipIdx = responseChunks.length; // remember this chip's position
                        responseChunks.push({ type: 'rs', subtype: 'reasoning_summary', content: 'Thinking...' });
                        updateMessages((prevMessages) =>
                          prevMessages.map((msg) =>
                            msg.timestamp === messageKey && msg.direction === 'received'
                              ? { ...msg, content: JSON.stringify(responseChunks), hasData, references: Array.from(references) }
                              : msg
                          )
                        );
                      }
                      startIndex = i + 1;
                      continue;
                    }

                    // Early tool call preview — show chip as soon as model starts building a tool call
                    if (parsedChunk.subtype === 'tool_call_start') {
                      // Flush the current buffer so pending chunks land before the preview chip
                      if (currentType !== null) {
                        responseChunks.push({
                          type: currentType,
                          subtype: currentSubtype,
                          content: currentContent,
                          ...currentAttributes
                        });
                        currentType = null;
                        currentSubtype = null;
                        currentContent = '';
                        currentAttributes = {};
                      }
                      responseChunks.push({
                        type: 'tc_preview',
                        toolCallId: parsedChunk.toolCallId,
                        label: parsedChunk.label
                      });
                      updateMessages((prevMessages) =>
                        prevMessages.map((msg) =>
                          msg.timestamp === messageKey && msg.direction === 'received'
                            ? { ...msg, content: JSON.stringify(responseChunks), hasData, references: Array.from(references) }
                            : msg
                        )
                      );
                      startIndex = i + 1;
                      continue;
                    }

                    // Handle forecast progress updates
                    if (parsedChunk.subtype === 'forecast_progress') {
                      if (callbacks.onForecastProgress) {
                        try {
                          callbacks.onForecastProgress(parsedChunk);
                        } catch (err) {
                          console.error('Error handling forecast progress:', err);
                        }
                      }
                      // Silently consumed — tool call chips in the message bubble are
                      // the UI indicator now; no spinner label needed here.
                      startIndex = i + 1;
                      continue;
                    }

                    // Handle checklist item updates
                    if (parsedChunk.subtype === 'checklist_update') {
                      if (callbacks.onChecklistUpdate && Array.isArray(parsedChunk.checklist)) {
                        try {
                          callbacks.onChecklistUpdate(parsedChunk.checklist);
                        } catch (err) {
                          console.error('Error handling checklist update:', err);
                        }
                      }
                      startIndex = i + 1;
                      continue;
                    }
                    
                    if (parsedChunk.subtype === 'report_initialized') {
                      // If this processing message contains a report-snippet, render it (and enter in-progress)
                      if (pIsSnippet) {
                        // Enter in-progress mode to suppress subsequent tool/proc UI
                        reportBuild.inProgress = true;
                        // Replace message content with this snippet (acts as latest)
                        updateMessages((prevMessages) =>
                          prevMessages.map((msg) =>
                            msg.timestamp === messageKey && msg.direction === 'received'
                              ? {
                                  ...msg,
                                  content: ptxt,
                                  hasData: hasData,
                                  references: Array.from(references)
                                }
                              : msg
                          )
                        );
                        reportBuild.snippetShown = true;
                        // Clear any processing banner
                        try { setShowProcessingMessage(false); } catch (_) {}
                        try { setProcessingMessage(''); } catch (_) {}
                        startIndex = i + 1;
                        continue;
                      }
                    }
                    
                    // While a report is in-progress, suppress other processing/tool action UI
                    if (reportBuild.inProgress) {
                      startIndex = i + 1;
                      continue;
                    }
                    // Handle streaming actions for tool_call and tool_action
                    if (parsedChunk.subtype === 'tool_call' || parsedChunk.subtype === 'tool_action') {
                      streamingActions.push(parsedChunk);
                      if (setStreamingActionData) {
                        setStreamingActionData([...streamingActions]);
                      }
                      setShowProcessingMessage(true);
                    } else {
                      // Handle regular processing messages (backward compatibility)
                      setProcessingMessage(parsedChunk.txt);
                      setShowProcessingMessage(true);
                    }
                    startIndex = i + 1;
                    continue;
                  } else {
                    // Non-'p' chunk detected - add it to streamingActions to trigger finished state
                    if (streamingActions.length > 0 && setStreamingActionData) {
                      streamingActions.push(parsedChunk);
                      setStreamingActionData([...streamingActions]);
                    }
                  }
                  
                  // Handle metadata events (e.g., report initialization)
                  if (parsedChunk.type === 'meta') {
                    if (parsedChunk.subtype === 'report_initialized' && parsedChunk.reportId) {
                      // Enter in-progress mode and clear any existing processing message
                      // if (parsedChunk.reportType === 'deep-research') {
                      //   reportBuild.inProgress = true;
                      //   try { setShowProcessingMessage(false); } catch (_) {}
                      //   try { setProcessingMessage(''); } catch (_) {}
                      // }
                      if (callbacks.onReportInitialized) {
                        try { callbacks.onReportInitialized(parsedChunk); } catch (_) {}
                      }
                    }
                    if (parsedChunk.subtype === 'forecast_step_completed') {
                      // Handle forecast step completion (silent update - no banner)
                      if (callbacks.onForecastProgress) {
                        try {
                          callbacks.onForecastProgress(parsedChunk);
                        } catch (err) {
                          console.error('Error handling forecast step completion:', err);
                        }
                      }
                    }
                    if (parsedChunk.subtype === 'forecast_calendar_defined') {
                      // Handle forecast calendar definition (silent update - no banner)
                      if (callbacks.onForecastProgress) {
                        try {
                          callbacks.onForecastProgress(parsedChunk);
                        } catch (err) {
                          console.error('Error handling forecast calendar definition:', err);
                        }
                      }
                    }
                    if (parsedChunk.subtype === 'report_completed') {
                      if (parsedChunk.reportType === 'deep-research') {
                        // If the completion meta includes a report-snippet, render it immediately
                        const mtxt = parsedChunk.txt || '';
                        const mIsSnippet = typeof mtxt === 'string' && mtxt.includes('```report-snippet');
                        if (mIsSnippet) {
                          // Persist snippet in the accumulated chunk list so later updates do not overwrite it
                          try {
                            responseChunks.push({ type: 'p', subtype: 'report_completed', content: mtxt });
                          } catch (_) {}
                          const immediateChunks = [
                            ...responseChunks,
                            ...(currentType !== null
                              ? [{
                                    type: currentType,
                                    subtype: currentSubtype,
                                    content: currentContent,
                                    ...currentAttributes
                                  }]
                              : [])
                          ];
                          updateMessages((prevMessages) =>
                            prevMessages.map((msg) =>
                              msg.timestamp === messageKey && msg.direction === 'received'
                                ? {
                                    ...msg,
                                    content: JSON.stringify(immediateChunks),
                                    hasData: hasData,
                                    references: Array.from(references)
                                  }
                                : msg
                            )
                          );
                          reportBuild.inProgress = false;
                          reportBuild.snippetShown = true;
                          try { setShowProcessingMessage(false); } catch (_) {}
                          try { setProcessingMessage(''); } catch (_) {}
                        }
                      }
                      if (callbacks.onReportCompleted) {
                        try { callbacks.onReportCompleted(parsedChunk); } catch (_) {}
                      }
                    }
                    startIndex = i + 1;
                    continue;
                  }

                  // Handle task management events (applies to Threads and TaskManager)
                  // if (parsedChunk.subtype === 'create_task') {
                  //   try {
                  //     const { task } = parsedChunk.toolContent?.task || {};
                  //     if (task) {
                  //       if (callbacks.onTaskCreate) {
                  //         callbacks.onTaskCreate(task);
                  //       }
                  //     }
                  //   } catch (e) {
                  //     console.error('Failed to process create_task toolContent', e);
                  //   }
                  //   startIndex = i + 1;
                  //   continue;
                  // }

                  // if (parsedChunk.subtype === 'update_task') {
                  //   try {
                  //     const { task } = parsedChunk.toolContent?.task || {};
                  //     if (task) {
                  //       if (callbacks.onTaskUpdate) {
                  //         callbacks.onTaskUpdate(task);
                  //       }
                  //     }
                  //   } catch (e) {
                  //     console.error('Failed to process update_task toolContent', e);
                  //   }
                  //   startIndex = i + 1;
                  //   continue;
                  // }

                  // if (parsedChunk.subtype === 'delete_task') {
                  //   try {
                  //     let taskId;
                  //     const tc = parsedChunk.toolContent?.task || {};
                  //     if (tc.task && tc.task.id) {
                  //       taskId = tc.task.id;
                  //     } else if (typeof tc.message === 'string') {
                  //       const match = tc.message.match(/Task\s+([A-Za-z0-9_-]+)\s+deleted/i);
                  //       if (match) taskId = match[1];
                  //     }
                  //     if (taskId) {
                  //       if (callbacks.onTaskDelete) {
                  //         callbacks.onTaskDelete(taskId);
                  //       }
                  //     }
                  //   } catch (e) {
                  //     console.error('Failed to process delete_task toolContent', e);
                  //   }
                  //   startIndex = i + 1;
                  //   continue;
                  // }

                  // Handle component events (applies to TaskManager and DashboardManager)
                  if (parsedChunk.subtype === 'comp') {
                    // While a report is in-progress, suppress component updates
                    if (reportBuild.inProgress) {
                      startIndex = i + 1;
                      continue;
                    }
                    if (callbacks.onComponentUpdate) {
                      // Support both single-object and array payloads
                      const internalUpdates = Array.isArray(parsedChunk.internal)
                        ? parsedChunk.internal
                        : [parsedChunk.internal];
                      
                      callbacks.onComponentUpdate(internalUpdates, parsedChunk);
                    }
                    startIndex = i + 1;
                    continue;
                  }

                  // Handle component deletion events
                  if (parsedChunk.subtype === 'comp_delete') {
                    if (reportBuild.inProgress) {
                      startIndex = i + 1;
                      continue;
                    }
                    if (callbacks.onComponentDelete) {
                      const deletedId = parsedChunk.deletedComponentId || 
                                       parsedChunk?.component?.id || 
                                       parsedChunk?.internal?.id;
                      if (deletedId) {
                        callbacks.onComponentDelete(deletedId);
                      }
                    }
                    startIndex = i + 1;
                    continue;
                  }

                  // If report is in-progress, only allow the initial report-snippet to show
                  if (reportBuild.inProgress) {
                    const txt = parsedChunk.txt || '';
                    const isSnippet = typeof txt === 'string' && txt.includes('```report-snippet');
                    if (isSnippet) {
                      // Always replace the content with the latest snippet (e.g., from send_report)
                      updateMessages((prevMessages) =>
                        prevMessages.map((msg) =>
                          msg.timestamp === messageKey && msg.direction === 'received'
                            ? { 
                                ...msg, 
                                content: txt,
                                hasData: hasData,
                                references: Array.from(references)
                              }
                            : msg
                        )
                      );
                      reportBuild.snippetShown = true;
                    }
                    startIndex = i + 1;
                    continue;
                  }

                  const text = parsedChunk.txt || '';
                  const type = parsedChunk.type;
                  const subtype = parsedChunk.subtype;

                  // Capture all attributes present in the chunk
                  const rawIncoming = parsedChunk;
                  const incomingAttributes = { ...rawIncoming };
                  if (Object.prototype.hasOwnProperty.call(incomingAttributes, 'resultForAgent')) {
                    if (!Object.prototype.hasOwnProperty.call(incomingAttributes, 'toolContent')) {
                      incomingAttributes.toolContent = incomingAttributes.resultForAgent;
                    }
                    delete incomingAttributes.resultForAgent;
                  }
                  // We store text separately as content, so drop txt to avoid duplication
                  delete incomingAttributes.txt;

                  // Collect references if present on this chunk (for Threads)
                  try {
                    const refs = incomingAttributes?.references;
                    if (Array.isArray(refs) && refs.length > 0) {
                      refs.forEach((r) => references.add(r));
                    }
                  } catch (_) {}
                  
                  // If this is a new type or subtype, save the previous chunk and start a new one
                  if ((type !== currentType || subtype !== currentSubtype) && currentType !== null) {
                    responseChunks.push({
                      type: currentType,
                      subtype: currentSubtype,
                      content: currentContent,
                      ...currentAttributes
                    });
                    currentContent = text;
                    currentAttributes = { ...incomingAttributes };
                  } else {
                    // Add to current content
                    currentContent += text;
                    // Merge attributes (later values win)
                    currentAttributes = { ...currentAttributes, ...incomingAttributes };
                  }
                  
                  currentType = type;
                  currentSubtype = subtype;
                  
                  // Hide processing message when we get actual content
                  if (currentContent) {
                    setShowProcessingMessage(false);
                  }
                  
                  // Update the message in the UI with all chunks so far
                  const updatedChunks = [
                    ...responseChunks,
                    {
                      type: currentType,
                      subtype: currentSubtype,
                      content: currentContent,
                      ...currentAttributes
                    }
                  ];
                  
                  updateMessages((prevMessages) =>
                    prevMessages.map((msg) =>
                      msg.timestamp === messageKey && msg.direction === 'received'
                        ? { 
                            ...msg, 
                            content: JSON.stringify(updatedChunks), 
                            hasData: hasData, 
                            references: Array.from(references) 
                          }
                        : msg
                    )
                  );
                  
                } catch (e) {
                  console.error('Error parsing JSON:', e, 'JSON string:', jsonStr);
                }
                
                startIndex = i + 1;
              }
            }
          }
        }
        
        // Keep remaining incomplete JSON in buffer
        buffer = buffer.substring(startIndex);
      }
    }
    
    // Final update to ensure we have the complete message
    if (currentType !== null && currentContent && currentType !== 'tc' && currentType !== 'tr') {
      responseChunks.push({
        type: currentType,
        subtype: currentSubtype,
        content: currentContent,
        ...currentAttributes
      });
      
      updateMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.timestamp === messageKey && msg.direction === 'received'
            ? { 
                ...msg, 
                content: JSON.stringify(responseChunks), 
                hasData: hasData, 
                references: Array.from(references) 
              }
            : msg
        )
      );
    }
  } catch (error) {
    console.error('Error in stream processing:', error);
    if (callbacks.onError) callbacks.onError(error);
  }
};
